import React, { useCallback, useEffect } from 'react';
import { StreamLevels } from '../..';
import Hls from 'hls.js';
import checkClick from '@src/ds/Utils/CheckClick';

const getHTML = (element: Element) => {
  const wrapper = document.createElement('div');
  wrapper.appendChild(element);
  return wrapper.innerHTML;
}

export type ConfigurationProps = {
  enabled: boolean;
  hls: Hls;
  code: string;
  levels: StreamLevels;
  currentLevel: number;
  subtitles: {
    language: string;
    label: string;
  }[];
  hasAIOpened: boolean;
};

const subtitleModels = [1, 2, 3, 4];
const subtitleSizes = [1, 2, 3, 4, 5];

const Configuration = ({ enabled, hls, code, levels, currentLevel, subtitles, hasAIOpened }: ConfigurationProps) => {
  const [subtitleModel, setSubtitleModel] = React.useState<any>(localStorage.getItem('smartPlayerSubtitleModel') || 1);
  const [subtitleSize, setSubtitleSize] = React.useState<any>(localStorage.getItem('smartPlayerSubtitleSize') || 3);
  const [velocity, setVelocity] = React.useState<number>(parseInt(localStorage.getItem('smartPlayerVelocity') || '1'));
  const [quality, setQuality] = React.useState<string>('auto');
  const [language, setLanguage] = React.useState<string>('Desativado');
  const [selectableLevels, setSelectableLevels] = React.useState<StreamLevels>([]);

  useEffect(() => {
    // const newLevels = levels.filter((level) => level. <= 720);
    if (levels.length === 0) {
      return;
    }

    const newLevels: StreamLevels = [];
    const addedLevels: string[] = [];
    const orderedLevels = levels.sort((a: any, b: any) => a.bitrate > b.bitrate ? 1 : -1);

    for (let i = 0; i < orderedLevels.length; i++) {
      const currentLevel = orderedLevels[i];
      const currentResolution = (currentLevel.height).toString();

      if (addedLevels.includes((currentResolution).toString())) {
        continue;
      }

      addedLevels.push(currentResolution);
      newLevels.push(currentLevel);
    }

    setSelectableLevels(newLevels);
  }, [hls, levels]);

  useEffect(() => {
    if (!code) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    localStorage.setItem('smartPlayerSubtitleModel', subtitleModel);

    const captionsElement = rootElement.querySelector('.smartplayer__captions');

    subtitleModels.forEach((model) => captionsElement?.classList.remove(`smartplayer__captions--model-${model}`));

    captionsElement?.classList.add(`smartplayer__captions--model-${subtitleModel}`);
  }, [subtitleModel, code]);

  useEffect(() => {
    if (!code) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    localStorage.setItem('smartPlayerSubtitleSize', subtitleSize);

    const captionsElement = rootElement.querySelector('.smartplayer__captions');

    subtitleSizes.forEach((size) => captionsElement?.classList.remove(`smartplayer__captions--font-${size}`));

    captionsElement?.classList.add(`smartplayer__captions--font-${subtitleSize}`);
  }, [subtitleSize, code]);

  useEffect(() => {
    localStorage.setItem('smartPlayerVelocity', (velocity).toString());
  }, [velocity]);

  const checkAfterClick = useCallback((event: any) => {
    if (event.target.classList.contains('smartplayer__configurationItem')
    || event.target?.parentElement?.classList.contains('smartplayer__configurationItem')) {
      return;
    }

    if (checkClick(event)) {
      onConfigClick(event, true);
    }
  }, [code]);

  const onVelocityChange = useCallback((event: any) => {
    event.preventDefault();
    event.stopPropagation();

    if (!hls || !hls.media) {
      return;
    }

    const newVelocity = parseFloat(event.target.value);
    hls.media.playbackRate = newVelocity;
    setVelocity(newVelocity);
  }, [hls]);

  const onQualityChange = useCallback((event: any) => {
    event.preventDefault();
    event.stopPropagation();

    if (!hls || !hls.media) {
      return;
    }

    const newQuality = parseInt(event.target.value === 'auto' ? -1 : event.target.value, 10);

    hls.currentLevel = newQuality;
    hls.media.playbackRate = velocity;
    hls.media.play();

    setQuality((newQuality).toString());

  }, [hls, velocity]);

  const onConfigurationSelect = useCallback((event: any) => {
    event.preventDefault();
    event.stopPropagation();

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const configurationContainer = rootElement?.querySelector('.smartplayer__configuration');

    if (!event?.target?.checked) {
      configurationContainer?.classList.add('smartplayer__configuration--opened');

      if (event.target.id === 'quality') {
        configurationContainer?.classList.remove('smartplayer__velocity--opened');
      } else if (event.target.id === 'velocity') {
        configurationContainer?.classList.remove('smartplayer__quality--opened');
      } else if (event.target.id === 'subtitle') {
        configurationContainer?.classList.remove('smartplayer__subtitle--opened');
      }

      return;
    }

    configurationContainer?.classList.remove('smartplayer__configuration--opened');

    if (event.target.id === 'quality') {
      configurationContainer?.classList.add('smartplayer__quality--opened');
      configurationContainer?.classList.remove('smartplayer__velocity--opened');
      configurationContainer?.classList.remove('smartplayer__subtitle--opened');
    } else if (event.target.id === 'velocity') {
      configurationContainer?.classList.remove('smartplayer__quality--opened');
      configurationContainer?.classList.add('smartplayer__velocity--opened');
      configurationContainer?.classList.remove('smartplayer__subtitle--opened');
    } else if (event.target.id === 'subtitle') {
      configurationContainer?.classList.remove('smartplayer__quality--opened');
      configurationContainer?.classList.remove('smartplayer__velocity--opened');
      configurationContainer?.classList.add('smartplayer__subtitle--opened');
    }
  }, [code]);

  const onBackButton = useCallback((event: any) => {
    event.preventDefault();
    event.stopPropagation();

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const configurationContainer = rootElement?.querySelector('.smartplayer__configuration');
    configurationContainer?.classList.remove('smartplayer__quality--opened');
    configurationContainer?.classList.remove('smartplayer__velocity--opened');
    configurationContainer?.classList.remove('smartplayer__subtitle--opened');
    configurationContainer?.classList.add('smartplayer__configuration--opened');

    const quality = rootElement.querySelector('#quality') as HTMLInputElement;

    if (quality) {
      quality.checked = false;
    }

    const velocity = rootElement.querySelector('#velocity') as HTMLInputElement;

    if (velocity) {
      velocity.checked = false;
    }

    const subtitle = rootElement.querySelector('#subtitle') as HTMLInputElement;

    if (subtitle) {
      subtitle.checked = false;
    }
  }, [code]);

  const onConfigClick = useCallback((event: any, parentElement?: boolean) => {
    if (!parentElement) {
      event.preventDefault();
      event.stopPropagation();
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    const volumeButton = rootElement.querySelector('.smartplayer__volume') as HTMLButtonElement;

    if (volumeButton) {
      volumeButton.classList.remove('smartplayer__volume--opened');
    }

    const aiButton = rootElement.querySelector('.smartplayer__ai') as HTMLButtonElement;

    if (aiButton) {
      aiButton.classList.remove('smartplayer__ai--opened');
    }

    const menuElement = rootElement.querySelector('.smartplayer__configuration') as HTMLDivElement;

    const hasOpened = menuElement.classList.contains('smartplayer__configuration--opened')
                   || menuElement.classList.contains('smartplayer__quality--opened')
                   || menuElement.classList.contains('smartplayer__velocity--opened')
                   || menuElement.classList.contains('smartplayer__subtitle--opened');

    const quality = rootElement.querySelector('#quality') as HTMLInputElement;

    if (quality) {
      quality.checked = false;
    }

    const velocity = rootElement.querySelector('#velocity') as HTMLInputElement;

    if (velocity) {
      velocity.checked = false;
    }

    const subtitle = rootElement.querySelector('#subtitle') as HTMLInputElement;

    if (subtitle) {
      subtitle.checked = false;
    }

    if (hasOpened) {

      // if (hasAIOpened) {
      //   // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');
      // } else {
      //   // rootElement.querySelector('.smartplayer__footer')?.classList.remove('smartplayer__footer--dropdown');
      // }

      menuElement.classList.remove('smartplayer__configuration--opened');
      menuElement.classList.remove('smartplayer__quality--opened');
      menuElement.classList.remove('smartplayer__velocity--opened');
      menuElement.classList.remove('smartplayer__subtitle--opened');
      return;
    }

    // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');

    menuElement.classList.toggle('smartplayer__configuration--opened');

    const eventLeave = new MouseEvent('mouseleave', {
      bubbles: true,
      cancelable: true,
      view: window
    });

    rootElement.querySelector('.smartplayer__controlbar')?.dispatchEvent(eventLeave);
  }, [code, hasAIOpened]);

  useEffect(() => {
    if (!hls || !levels) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const configurationElement = rootElement.querySelectorAll('[name="configuration"]');
    const velocityElement = rootElement.querySelectorAll('[name="velocity"]');
    const qualityElement = rootElement.querySelectorAll('[name="quality"]');
    const backButtonsElement = rootElement.querySelectorAll('.smartplayer__subConfigurationBack');

    (velocityElement || []).forEach((element) => {
      element?.addEventListener('change', onVelocityChange);
    });

    // console.log('test qualityElement', qualityElement);

    (qualityElement || []).forEach((element: any) => {
      element?.addEventListener('change', onQualityChange);
    });

    (configurationElement || []).forEach((element: any) => {
      element?.addEventListener('change', onConfigurationSelect);
    });

    (backButtonsElement || []).forEach((element) => {
      element.addEventListener('click', onBackButton);
    });

    return () => {
      (velocityElement || []).forEach((element) => {
        element?.removeEventListener('change', onVelocityChange);
      });

      (qualityElement || []).forEach((element) => {
        element?.removeEventListener('change', onQualityChange);
      });

      (configurationElement || []).forEach((element) => {
        element?.removeEventListener('change', onConfigurationSelect);
      });

      (backButtonsElement || []).forEach((element) => {
        element.removeEventListener('click', onBackButton);
      });
    }
  }, [code, hls, levels, selectableLevels, onQualityChange, onVelocityChange, onConfigurationSelect, onBackButton]);

  const onCueChange = useCallback((event: any) => {
    const element = event?.target as any;

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const captionsElement = rootElement.querySelector('.smartplayer__captions');

    if (element && element?.mode === 'showing') {
      // eslint-disable-next-line no-param-reassign
      element.mode = 'hidden';
    }

    const cues =  Array.from(element?.activeCues || [])
                        .map((cue: any) => cue.getCueAsHTML())
                        .map(getHTML);

    // console.log('cues', cues);

    // const subtitle = rootElement.querySelector(`[data-subtitle="${element.id}"]`);

    const content = (cues.map((cueText) => cueText.trim()).join(' ')
                        .split('\n').join(' ') || '').toString().trim();
    const changed = content !== captionsElement?.innerHTML;

    if (changed && captionsElement) {
      captionsElement.innerHTML = '';

      const caption = document.createElement('span');
      caption.classList.add('smartplayer__caption');
      caption.innerHTML = content;
      captionsElement?.append(caption);
    }
  }, [code, hls, hls?.media]);

  const onSubtitleChange = useCallback((event: any) => {
    if (!hls || !hls.media) {
      return;
    }

    // debugger;

    // console.log('onSubtitleChange', event.target.value, hls.media.textTracks);

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const captionsElement = rootElement.querySelector('.smartplayer__captions');

    const newSubtitle = (event.target as HTMLSelectElement).value;
    const tracks = new Array(...hls.media.textTracks);
    const index = tracks.findIndex((track) => track.language === newSubtitle);
    const activeSubtitle = hls.subtitleTrack;

    if (activeSubtitle >= 0) {
      tracks.map((item) => {
        item.mode = 'disabled';
        // item.default = false;
        // item.attributes.add('active', false);
      });
    }

    if (index >= 0) {
      hls.subtitleTrack = index;
      hls.media.textTracks[index].mode = 'hidden';
      setLanguage(hls.subtitleTracks[index].lang === 'TRANSCRIPTION' ? 'Transcrição' : hls.subtitleTracks[index].name);
    }

    if (captionsElement) {
      captionsElement.innerHTML = '';
    }
  }, [code, hls, hls?.media]);

  useEffect(() => {
    if (!hls || !hls.media || subtitles.length === 0) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const subtitlesElement = rootElement.querySelectorAll('[name="subtitle"]');

    // console.log('test', subtitlesElement);

    (subtitlesElement || []).forEach((element, index) => {
      if (hls?.media?.textTracks[index]) {
        hls?.media?.textTracks[index].addEventListener('cuechange', onCueChange);
      }

      element.addEventListener('change', onSubtitleChange);
    });

    return () => {
      (subtitlesElement || []).forEach((element, index) => {
        if (hls?.media?.textTracks[index]) {
          hls?.media?.textTracks[index].addEventListener('cuechange', onCueChange);
        }

        element.removeEventListener('change', onSubtitleChange);
      });
    };
  }, [hls, hls?.media, code, onSubtitleChange, onCueChange, subtitles]);

  return enabled ? (
    <div className="smartplayer__configuration" onClick={checkAfterClick}>
      <button className="smartplayer__bar-button" type="button" onClick={onConfigClick} >
        <div className="icon icon--configuration">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512" fill="currentColor">
            <path d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"></path>
          </svg>
        </div>
      </button>
      <div className="smartplayer__dropdownContainer">
        <div className="smartplayer__configurationList">
          <div className="smartplayer__configurationItem smartplayer__configurationItem--quality">
            <input type="radio" id="quality" name="configuration" value="quality" defaultChecked={false} />
            <label className="smartplayer__configurationItemTitle" htmlFor="quality">
              Qualidade
              <div className="smartplayer__configurationItemValue">
                <span className="smartplayer__configurationItemValueText">
                  {
                    quality !== 'auto' && currentLevel != null && levels != null ?
                    `${levels[currentLevel].height}p` :
                    `Auto ${
                      currentLevel != null && levels != null && levels[currentLevel] ?
                      (
                        `(${levels[currentLevel].height}p)`
                      ) : null
                    }`
                  }
                </span>
                <span className="smartplayer__configurationItemBox"></span>
              </div>
            </label>
          </div>
          <div className="smartplayer__configurationItem smartplayer__configurationItem--velocity">
            <input type="radio" id="velocity" name="configuration" value="velocity" defaultChecked={false} />
            <label className="smartplayer__configurationItemTitle" htmlFor="velocity">
              Velocidade
              <div className="smartplayer__configurationItemValue">
                <span className="smartplayer__configurationItemValueText">{
                  velocity === 1 ? 'Normal' : `${velocity}x`
                }</span>
                <span className="smartplayer__configurationItemBox"></span>
              </div>
            </label>
          </div>
          <div className="smartplayer__configurationItem smartplayer__configurationItem--subtitle">
            <input type="radio" id="subtitle" name="configuration" value="subtitle" defaultChecked={false} />
            <label className="smartplayer__configurationItemTitle" htmlFor="subtitle">
              Legendas
              <div className="smartplayer__configurationItemValue">
                <span className="smartplayer__configurationItemValueText">{language}</span>
                <span className="smartplayer__configurationItemBox"></span>
              </div>
            </label>
          </div>
        </div>
      </div>
      <div className="smartplayer__dropdownContainerQuality">
        <div className="smartplayer__subConfigurationList">
          <div className="smartplayer__subConfigurationHeader">
            <button className="smartplayer__subConfigurationBack"></button>
            <h3 className="smartplayer__subConfigurationTitle">Qualidade</h3>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="auto" name="quality" value="auto" defaultChecked={quality === 'auto'} />
            <label htmlFor="auto"><span className="smartplayer__configurationCheck"></span>Auto</label>
          </div>
          {
            (selectableLevels || []).map((level: any) => (
              <div className="smartplayer__configurationItem">
                <input type="radio" id={`quality--${level?.key}`} name="quality" value={level?.key}  defaultChecked={quality === (level?.key).toString()} />
                <label htmlFor={`quality--${level?.key}`}><span className="smartplayer__configurationCheck"></span>{level.height}p</label>
              </div>
            ))
          }
        </div>
      </div>
      <div className="smartplayer__dropdownContainerVelocity">
        <div className="smartplayer__subConfigurationList">
          <div className="smartplayer__subConfigurationHeader">
            <button className="smartplayer__subConfigurationBack"></button>
            <h3 className="smartplayer__subConfigurationTitle">Velocidade</h3>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--05" name="velocity" value="0.5" defaultChecked={velocity === 0.5} />
            <label htmlFor="velocity--05"><span className="smartplayer__configurationCheck"></span>0.5x</label>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--075" name="velocity" value="0.75" defaultChecked={velocity === 0.75} />
            <label htmlFor="velocity--075"><span className="smartplayer__configurationCheck"></span>0.75x</label>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--normal" name="velocity" value="1" defaultChecked={velocity === 1} />
            <label htmlFor="velocity--normal"><span className="smartplayer__configurationCheck"></span>Normal</label>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--125" name="velocity" value="1.25" defaultChecked={velocity === 1.25} />
            <label htmlFor="velocity--125"><span className="smartplayer__configurationCheck"></span>1.25x</label>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--15" name="velocity" value="1.5" defaultChecked={velocity === 1.5} />
            <label htmlFor="velocity--15"><span className="smartplayer__configurationCheck"></span>1.5x</label>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--175" name="velocity" value="1.75" defaultChecked={velocity === 1.75} />
            <label htmlFor="velocity--175"><span className="smartplayer__configurationCheck"></span>1.75x</label>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="velocity--2" name="velocity" value="2" defaultChecked={velocity === 2} />
            <label htmlFor="velocity--2"><span className="smartplayer__configurationCheck"></span>2x</label>
          </div>
        </div>
      </div>
      <div className="smartplayer__dropdownContainerSubtitle">
        <div className="smartplayer__subConfigurationList">
          <div className="smartplayer__subConfigurationHeader">
            <button className="smartplayer__subConfigurationBack"></button>
            <h3 className="smartplayer__subConfigurationTitle">Legenda</h3>
          </div>
          <div className="smartplayer__configurationItem">
            <input type="radio" id="disabled" name="subtitle" value="" defaultChecked />
            <label htmlFor="disabled"><span className="smartplayer__configurationCheck"></span>Desativado</label>
          </div>
          {(subtitles || []).map((subTitle) => (
            <div className="smartplayer__configurationItem">
              <input type="radio" id={subTitle.language} name="subtitle" value={subTitle.language} />
              <label htmlFor={subTitle.language}><span className="smartplayer__configurationCheck"></span>{subTitle.label}</label>
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : null;
};

export default Configuration;
